type NiceDomain = {
  niceMin: number
  niceMax: number
  ticks: number[]
}

/**
 * Extends the given domain to nice, rounded numbers, with evenly distributed
 * ticks.
 */
export const getNiceDomain = (min: number, max: number): NiceDomain => {
  const range = Math.max(1, max - min)
  const exponent = Math.floor(Math.log10(range))
  let factor = Math.pow(10, exponent)

  if (range / factor <= 1.0) {
    factor *= 2
  }

  let niceMin = Math.floor(min / factor) * factor

  if (niceMin - min < 0.5) {
    niceMin = Math.max(0, niceMin - factor)
  }

  let niceMax = Math.ceil(max / factor) * factor

  const ticks: number[] = []
  const niceRange = niceMax - niceMin
  const numTicks = getNumTicks(niceRange, factor)
  const interval = Math.ceil(niceRange / (numTicks - 1))

  for (let i = 0; i < numTicks; i++) {
    ticks.push(niceMin + i * interval)
  }

  // Adjust niceMax to match the last tick.
  niceMax = ticks[numTicks - 1]

  return { niceMin, niceMax, ticks }
}

/**
 * Scales an index to a position on the x-axis.
 */
export const scaleX = (params: {
  index: number
  width: number
  paddingLeft?: number
  paddingRight?: number
  numPoints: number
}): number => {
  const { index, width, paddingLeft = 0, paddingRight = 0, numPoints } = params
  const contentWidth = width - (paddingLeft + paddingRight)
  const scaled = (index / (numPoints - 1)) * contentWidth

  return paddingLeft + scaled
}

/**
 * Scales a value to a position on the y-axis.
 */
export const scaleY = (params: {
  value: number
  height: number
  paddingTop?: number
  min: number
  max: number
}): number => {
  const { value, height, paddingTop = 0, min, max } = params
  const contentHeight = height
  const offset = paddingTop ?? 0

  const y = contentHeight - ((value - min) / (max - min)) * contentHeight
  const y_ = y + offset

  return y_
}

/**
 * Given a range and a factor, determines the number of ticks to display on the
 * y-axis.
 */
const getNumTicks = (range: number, factor: number) => {
  if (range <= 2 * factor) {
    return 4
  }

  const remainder5 = range % 4
  const remainder4 = range % 3

  if (remainder5 === 0) {
    return 5
  }

  if (remainder4 === 0) {
    return 4
  }

  const diff5 = 5 - remainder5
  const diff4 = 4 - remainder4

  if (diff5 <= diff4 + 1) {
    return 5
  }

  return 4
}
